<script setup lang="ts">
import { computed } from "vue";

const props = defineProps<{
  /** 0-100 */
  percent: number;
  bytesSent: number;
  totalBytes: number;
  /** Optional smoothed throughput in bytes/second. When > 0, displayed
   *  next to the bytes-sent / total readout (e.g., "1.2 MB/s"). */
  bytesPerSec?: number;
  /** Status text to show next to the percentage (e.g., "Uploading", "Finalizing"). */
  label?: string;
}>();

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const clampedPercent = computed(() => Math.max(0, Math.min(100, props.percent)));
const sentLabel = computed(() => formatBytes(props.bytesSent));
const totalLabel = computed(() => formatBytes(props.totalBytes));
const rateLabel = computed(() =>
  props.bytesPerSec && props.bytesPerSec > 0
    ? `${formatBytes(props.bytesPerSec)}/s`
    : null,
);
</script>

<template>
  <div data-testid="upload-progress" class="space-y-1">
    <div class="flex justify-between text-sm text-slate-700">
      <span data-testid="upload-progress-label">{{ label ?? "Uploading" }}</span>
      <span data-testid="upload-progress-percent">{{ clampedPercent }}%</span>
    </div>
    <div
      class="h-2 rounded-full bg-slate-200 overflow-hidden"
      role="progressbar"
      :aria-valuenow="clampedPercent"
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <div
        data-testid="upload-progress-bar"
        class="h-full bg-blue-600 transition-[width] duration-200"
        :style="{ width: `${clampedPercent}%` }"
      ></div>
    </div>
    <div class="text-xs text-slate-500 flex justify-between gap-2">
      <span data-testid="upload-progress-bytes">{{ sentLabel }} / {{ totalLabel }}</span>
      <span
        v-if="rateLabel"
        data-testid="upload-progress-rate"
      >{{ rateLabel }}</span>
    </div>
  </div>
</template>
